import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Box,
} from '@mui/material'
import { EMAIL_TYPE_OPTIONS } from '../../../utils/emailTypeOptions'

const CONTENT_TYPES = [
  { value: 'HTML', label: 'HTML' },
  { value: 'MARKDOWN', label: 'Markdown' },
]

const EmailTemplateDialog = ({ open, template, onClose, onSubmit }) => {
  const [submitting, setSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    content: '',
    contentType: 'HTML',
    emailType: 'NO_REPLY',
  })

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || '',
        subject: template.subject || '',
        content: template.content || '',
        contentType: template.contentType || 'HTML',
        emailType: template.emailType || 'NO_REPLY',
      })
    } else {
      setFormData({
        name: '',
        subject: '',
        content: '',
        contentType: 'HTML',
        emailType: 'NO_REPLY',
      })
    }
  }, [template, open])

  const handleChange = (field) => (e) => {
    setFormData({
      ...formData,
      [field]: e.target.value,
    })
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      await onSubmit(formData)
    } catch (error) {
      console.error('Error submitting template:', error)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>
        {template ? 'Edit' : 'Create New'} Email Template
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Template Name'
                value={formData.name}
                onChange={handleChange('name')}
                required
                helperText='A unique name to identify this template'
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Subject'
                value={formData.subject}
                onChange={handleChange('subject')}
                required
                helperText='Email subject line (supports template variables like {{userName}})'
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Content Type</InputLabel>
                <Select
                  value={formData.contentType}
                  onChange={handleChange('contentType')}
                  label='Content Type'
                >
                  {CONTENT_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Email Type</InputLabel>
                <Select
                  value={formData.emailType}
                  onChange={handleChange('emailType')}
                  label='Email Type'
                >
                  {EMAIL_TYPE_OPTIONS.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Content'
                multiline
                rows={10}
                value={formData.content}
                onChange={handleChange('content')}
                required
                helperText={
                  formData.contentType === 'MARKDOWN'
                    ? 'Enter Markdown content. Supports template variables like {{userName}}, {{userEmail}}'
                    : 'Enter HTML content. Supports template variables like {{userName}}, {{userEmail}}'
                }
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleSubmit}
          disabled={submitting}
          sx={{
            bgcolor: '#232536',
            color: 'white',
            '&:hover': { bgcolor: '#1e2130' },
          }}
        >
          {submitting ? (
            <CircularProgress size={24} sx={{ color: 'white' }} />
          ) : template ? (
            'Update'
          ) : (
            'Create'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EmailTemplateDialog
